/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

const UPDATED_TOAST_KEY = "pwa-updated-toast";
const AUTO_UPDATE_CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;
const AUTO_UPDATE_RETRY_INTERVAL_MS = 30 * 1000;
const AUTO_UPDATE_MAX_DELAY_MS = 10 * 60 * 1000;

const isFormRoute = (pathname) =>
  pathname.endsWith("/new") ||
  pathname.includes("/edit/") ||
  pathname === "/sales/new" ||
  pathname === "/memberships/consume";

const isSafeToApplyUpdate = () => {
  if (typeof window === "undefined") return false;
  if (document.visibilityState === "hidden") return true;

  return !isFormRoute(window.location.pathname);
};

const PwaUpdaterContext = React.createContext(null);
const getInitialUpdatedToastState = () => {
  if (typeof window === "undefined") return false;
  if (window.sessionStorage.getItem(UPDATED_TOAST_KEY) !== "1") return false;
  window.sessionStorage.removeItem(UPDATED_TOAST_KEY);
  return true;
};

export function PwaUpdaterProvider({ children }) {
  const [justUpdated, setJustUpdated] = React.useState(getInitialUpdatedToastState);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const registrationRef = React.useRef(null);
  const intervalRef = React.useRef(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      registrationRef.current = registration ?? null;

      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }

      if (registration) {
        intervalRef.current = window.setInterval(() => {
          registration.update();
        }, AUTO_UPDATE_CHECK_INTERVAL_MS);
      }
    },
    onRegisterError(error) {
      console.log("SW registration error", error);
    },
  });

  React.useEffect(() => {
    setOfflineReady(false);
  }, [setOfflineReady]);

  React.useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  const dismissNeedRefresh = React.useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const dismissUpdatedMessage = React.useCallback(() => {
    setJustUpdated(false);
    setIsUpdating(false);
  }, []);

  const applyUpdate = React.useCallback(async () => {
    sessionStorage.setItem(UPDATED_TOAST_KEY, "1");
    setIsUpdating(true);
    await updateServiceWorker(true);
  }, [updateServiceWorker]);

  const checkForUpdates = React.useCallback(async () => {
    const registration = registrationRef.current;
    if (!registration) return false;

    setIsUpdating(true);

    try {
      await registration.update();

      if (registration.waiting) {
        await applyUpdate();
        return true;
      }

      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [applyUpdate]);

  React.useEffect(() => {
    if (!needRefresh || isUpdating) return undefined;

    let cancelled = false;
    const firstSeenAt = Date.now();
    let retryTimeoutId = null;

    const tryApplyUpdate = () => {
      if (cancelled || isUpdating) return;

      const delayedTooLong = Date.now() - firstSeenAt >= AUTO_UPDATE_MAX_DELAY_MS;
      if (isSafeToApplyUpdate() || delayedTooLong) {
        void applyUpdate();
        return;
      }

      retryTimeoutId = window.setTimeout(
        tryApplyUpdate,
        AUTO_UPDATE_RETRY_INTERVAL_MS,
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        tryApplyUpdate();
      }
    };

    const handleRouteChange = () => {
      window.setTimeout(tryApplyUpdate, 0);
    };

    retryTimeoutId = window.setTimeout(tryApplyUpdate, 0);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("popstate", handleRouteChange);
    window.addEventListener("app:navigation", handleRouteChange);

    return () => {
      cancelled = true;
      if (retryTimeoutId) {
        window.clearTimeout(retryTimeoutId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("popstate", handleRouteChange);
      window.removeEventListener("app:navigation", handleRouteChange);
    };
  }, [needRefresh, isUpdating, applyUpdate]);

  React.useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const dispatchNavigationEvent = () => {
      window.dispatchEvent(new Event("app:navigation"));
    };

    window.history.pushState = function pushState(...args) {
      const result = originalPushState.apply(this, args);
      dispatchNavigationEvent();
      return result;
    };

    window.history.replaceState = function replaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      dispatchNavigationEvent();
      return result;
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const value = {
    needRefresh,
    justUpdated,
    isUpdating,
    checkForUpdates,
    applyUpdate,
    dismissNeedRefresh,
    dismissUpdatedMessage,
  };

  return (
    <PwaUpdaterContext.Provider value={value}>
      {children}
    </PwaUpdaterContext.Provider>
  );
}

export function usePwaUpdater() {
  const context = React.useContext(PwaUpdaterContext);

  if (!context) {
    throw new Error("usePwaUpdater must be used within PwaUpdaterProvider");
  }

  return context;
}
